import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import {
  Button,
  Form,
  Input,
  Select,
  Typography,
  Checkbox,
  InputNumber,
} from 'antd';
import { isValidPhoneNumber } from 'libphonenumber-js';
// API
import { GET_ALL_AREAS_OF_INFLUENCE } from 'api/areaOfInfluence/queries';
import { GET_ALL_PSYCHOSOCIAL_KEYWORDS } from 'api/psychosocialKeyword/queries';
import { GET_HASHTAGS } from 'api/hashtags/queries';
import { EDIT_STORE_ADMIN } from 'api/store/mutations';
import { GET_INFLUENCER_CATEGORIES } from 'api/influencerCategory/queries';
// Constants
import { commonTimeFormat } from 'constants/global';
// Types
import {
  GetAllUserPsychosocialKeywords,
  GetAllUserPsychosocialKeywordsVariables,
} from 'api/psychosocialKeyword/types/GetAllUserPsychosocialKeywords';
import {
  GetAllAreasOfInfluence,
  GetAllAreasOfInfluenceVariables,
} from 'api/areaOfInfluence/types/GetAllAreasOfInfluence';
import {
  EditStoreAdmin,
  EditStoreAdminVariables,
} from 'api/store/types/EditStoreAdmin';
import {
  GetHashtags,
  GetHashtagsVariables,
} from 'api/hashtags/types/GetHashtags';
import { GetInfluencerCategories } from 'api/influencerCategory/types/GetInfluencerCategories';
import { UploadFile } from 'antd/es/upload/interface';
import { GetManageBioStore_adminStores_entities } from 'api/store/types/GetManageBioStore';
import {
  StoreGender,
  StoreStatus,
  StoreEditAdminInput,
} from 'api/graphql-global-types';
// Hooks
import { useGetAdmins, useGetSports } from 'hooks';
// Helpers
import {
  isEmptyString,
  isZero,
  PROFILE_GENDER_OPTIONS,
  getProfileGenderFormValue,
} from 'helpers/editStore';
import { createLocation, handlePlaceSelect, joinLocation } from 'helpers/place';
// UI
import UploadImageFile from 'ui/UploadImageFile/UploadImageFile';
import { errorNotification, successNotification } from 'ui/Notification';
import PlaceInput from 'components/common/PlaceInput/PlaceInput';
// Components
import PhoneNumberInput from 'components/common/PhoneInput/PhoneNumberInput';
import CroppedPictureInput from 'components/common/CroppedPictureInput/CroppedPictureInput';
import RichText from 'components/common/RichText/RichText';
import ProfileVisibilityInfo from 'components/common/ProfileVisibilityInfo';
// Styles
import './styles.scss';

const { Option } = Select;
const { Title } = Typography;

const buttonItemLayout = {
  wrapperCol: { span: 10, offset: 8 },
};

type EditContentCreatorFormProps = {
  contentCreator: GetManageBioStore_adminStores_entities | undefined;
};

type FormValues = {
  storeName?: string;
  sportType?: string[];
  country?: string;
  city?: string;
  uploadAvatarImage?: Array<UploadFile>;
  uploadCoverImage?: Array<UploadFile>;
  isOrphanPage: boolean;
  isForBrandDealsOnly: boolean;
  description?: string;
  hashtags?: number[];
  contactsFirstName?: string;
  contactsLastName?: string;
  contactsRole?: string;
  contactsEmail?: string;
  contactsPhoneNumber?: string;
  redirectSlugs?: string[];
  facebookLink: string;
  instagramLink: string;
  tiktokLink: string;
  website: string;
  twitterLink: string;
  socialMediaLink: string;
  youtubeLink: string;
  adConnected?: boolean;
  agencyName: string;
  agencyWebsite: string;
  agentEmail: string;
  agentName: string;
  facebookFollowing: number;
  gender: StoreGender;
  inbound: string;
  initialAssessment: number;
  instagramFollowing: number;
  latestAssessment: number;
  managedById: string;
  socialConnected: boolean;
  tiktokFollowing: number;
  twitterFollowing: number;
  youtubeFollowing: number;
  influencerCategoryIds?: number[];
  areasOfInfluence?: string[];
  psychosocialKeywords?: string[];
  state: string;
  isBirthDayKnown: boolean;
  isBirthMonthKnown: boolean;
  birthDate: string;
  designRequestsPerMonthLimit: number;
  signing?: string | null;
  socialPost?: string | null;
  appearance?: string | null;
  pressConferenceOrAmplifiedInterview?: string | null;
  watchParty?: string | null;
  affinities?: string | null;
  currentSponsors?: string | null;
  conflictsOfInterestsOrNonInterests?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

const EditContentCreatorForm = ({
  contentCreator,
}: EditContentCreatorFormProps): JSX.Element => {
  const [form] = Form.useForm();
  // eslint-disable-next-line
  const [formFields, setFormFields] = useState<FormValues | {}>({});
  const [pictureValidation, setPictureValidation] = useState<string>('');
  const uploadAvatarImage = form.getFieldValue('uploadAvatarImage');

  const { data: adminsData } = useGetAdmins({
    variables: {
      limit: 100,
      status: [StoreStatus.Active, StoreStatus.Pending, StoreStatus.Inactive],
    },
  });

  const { data: sportsData } = useGetSports();

  useEffect(() => {
    if (contentCreator) {
      form.setFieldsValue({
        storeName: contentCreator.storeDetails?.storeName,
        sportType: contentCreator.sports.map((item) => item.id.toString()),
        country: contentCreator.storeDetails?.country,
        state: contentCreator.storeDetails?.state,
        city: contentCreator.storeDetails?.city,
        uploadAvatarImage: contentCreator.storeDetails?.avatarURL,
        uploadCoverImage: contentCreator.storeDetails?.coverURL,
        isOrphanPage: contentCreator.isOrphanPage,
        isForBrandDealsOnly: contentCreator.isForBrandDealsOnly,
        description: contentCreator.storeDetails?.description,
        hashtags: contentCreator.hashtags.map((item) => item.id),
        contactsFirstName: contentCreator.firstName,
        contactsLastName: contentCreator.lastName,
        contactsRole: contentCreator?.storeDetails?.gymRole,
        contactsEmail: contentCreator.email,
        contactsPhoneNumber: contentCreator.phoneNumber,
        redirectSlugs: contentCreator.redirectSlugs || [],
        facebookLink: contentCreator?.storeDetails?.facebookLink,
        instagramLink: contentCreator?.storeDetails?.instagramLink,
        tiktokLink: contentCreator?.storeDetails?.tiktokLink,
        youtubeLink: contentCreator?.storeDetails?.youtubeLink,
        twitterLink: contentCreator?.storeDetails?.twitterLink,
        website: contentCreator?.storeDetails?.website,
        adConnected: contentCreator?.storeManagementDetails?.adConnected,
        agencyName: contentCreator.storeManagementDetails?.agencyName,
        agencyWebsite: contentCreator.storeManagementDetails?.agencyWebsite,
        agentEmail: contentCreator.storeManagementDetails?.agentEmail,
        agentName: contentCreator.storeManagementDetails?.agentName,
        inbound: contentCreator?.storeManagementDetails?.inbound,
        initialAssessment:
          contentCreator?.storeManagementDetails?.initialAssessment,
        latestAssessment:
          contentCreator?.storeManagementDetails?.latestAssessment,
        managedById: contentCreator?.storeManagementDetails?.managedById,
        socialConnected:
          contentCreator?.storeManagementDetails?.socialConnected,
        facebookFollowing: contentCreator?.storeDetails?.facebookFollowing,
        gender: getProfileGenderFormValue(contentCreator?.storeDetails?.gender),
        instagramFollowing: contentCreator?.storeDetails?.instagramFollowing,
        tiktokFollowing: contentCreator?.storeDetails?.tiktokFollowing,
        twitterFollowing: contentCreator?.storeDetails?.twitterFollowing,
        youtubeFollowing: contentCreator?.storeDetails?.youtubeFollowing,
        influencerCategoryIds: contentCreator.influencerCategories?.map(
          (item) => item.id
        ),
        areasOfInfluence: contentCreator.areasOfInfluence?.map(
          (item) => item.id
        ),
        psychosocialKeywords: contentCreator.psychosocialKeywords?.map(
          (item) => item.id
        ),
        designRequestsPerMonthLimit:
          contentCreator?.storeManagementDetails?.designRequestsPerMonthLimit,
        signing: contentCreator.storeManagementDetails?.signing || null,
        socialPost: contentCreator.storeManagementDetails?.socialPost || null,
        appearance: contentCreator.storeManagementDetails?.appearance || null,
        pressConferenceOrAmplifiedInterview:
          contentCreator.storeManagementDetails
            ?.pressConferenceOrAmplifiedInterview || null,
        watchParty: contentCreator.storeManagementDetails?.watchParty || null,
        affinities: contentCreator.storeManagementDetails?.affinities || null,
        currentSponsors:
          contentCreator.storeManagementDetails?.currentSponsors || null,
        conflictsOfInterestsOrNonInterests:
          contentCreator.storeManagementDetails
            ?.conflictsOfInterestsOrNonInterests || null,
        citySearch: joinLocation([
          contentCreator?.storeDetails?.city,
          contentCreator?.storeDetails?.state,
          contentCreator?.storeDetails?.country,
        ]),
        latitude: contentCreator?.storeDetails?.latitude || null,
        longitude: contentCreator?.storeDetails?.longitude || null,
      });
    }
  }, [contentCreator, form]);

  const { data } = useQuery<GetHashtags, GetHashtagsVariables>(GET_HASHTAGS, {
    variables: {
      keyword: '',
    },
    fetchPolicy: 'cache-and-network',
  });

  const [editStoreAdmin, { loading: editLoading }] = useMutation<
    EditStoreAdmin,
    EditStoreAdminVariables
  >(EDIT_STORE_ADMIN);

  const { data: influencerCategoriesData } = useQuery<GetInfluencerCategories>(
    GET_INFLUENCER_CATEGORIES,
    { fetchPolicy: 'cache-and-network' }
  );

  const { data: areaOfInfluenceData } = useQuery<
    GetAllAreasOfInfluence,
    GetAllAreasOfInfluenceVariables
  >(GET_ALL_AREAS_OF_INFLUENCE, {
    variables: {
      input: {
        keyword: '',
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: psychosocialKeywordData } = useQuery<
    GetAllUserPsychosocialKeywords,
    GetAllUserPsychosocialKeywordsVariables
  >(GET_ALL_PSYCHOSOCIAL_KEYWORDS, {
    variables: {
      input: {
        keyword: '',
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleFormSubmit = async (values: FormValues): Promise<void> => {
    const avatar =
      values.uploadAvatarImage?.length === 0
        ? null
        : values?.uploadAvatarImage?.[0]?.originFileObj;
    const cover =
      values.uploadCoverImage?.length === 0
        ? null
        : values?.uploadCoverImage?.[0]?.originFileObj;

    const storeEditInput: StoreEditAdminInput = {
      storeId: contentCreator?.id || '',
      userDetails: {
        firstName: values.contactsFirstName,
        lastName: values.contactsLastName,
        email: values.contactsEmail || '',
        phoneNumberE164: values.contactsPhoneNumber || '',
        sportIds: values?.sportType?.map(Number),
        hashtagIds: values.hashtags,
        isOrphanPage: values.isOrphanPage,
        isForBrandDealsOnly: values.isForBrandDealsOnly,
        redirects: values.redirectSlugs || [],
        influencerCategoryIds: values.influencerCategoryIds,
        areaOfInfluenceIds: values.areasOfInfluence || [],
        psychosocialKeywordsIds: values.psychosocialKeywords || [],
      },
      storeDetails: {
        location: createLocation(values.latitude, values.longitude),
        socialMediaLink: values.socialMediaLink,
        storeName: values.storeName,
        facebookLink: isEmptyString(values.facebookLink),
        instagramLink: isEmptyString(values.instagramLink),
        tiktokLink: isEmptyString(values.tiktokLink),
        youtubeLink: isEmptyString(values.youtubeLink),
        twitterLink: isEmptyString(values.twitterLink),
        website: isEmptyString(values.website),
        ...(values.uploadAvatarImage === contentCreator?.storeDetails?.avatarURL
          ? {}
          : { avatar: avatar }),
        ...(values.uploadCoverImage === contentCreator?.storeDetails?.coverURL
          ? {}
          : { cover: cover }),
        description: values.description || '',
        country: values.country || null,
        state: values.state || null,
        city: values.city || null,
        gymRole: values.contactsRole || '',
      },
      storeManagementDetails: {
        adConnected: values.adConnected || false,
        agencyName: isEmptyString(values.agencyName),
        agencyWebsite: isEmptyString(values.agencyWebsite),
        agentEmail: isEmptyString(values.agentEmail),
        agentName: isEmptyString(values.agentName),
        gender: values.gender,
        inbound: isEmptyString(values.inbound),
        managedById: values.managedById || null,
        socialConnected: values.socialConnected,
        initialAssessment: isZero(
          Number(values.initialAssessment) <= 5 &&
            Number(values.initialAssessment) >= 0
            ? Number(values.initialAssessment)
            : 0
        ),
        latestAssessment: isZero(
          Number(values.latestAssessment) <= 5 &&
            Number(values.latestAssessment) >= 0
            ? Number(values.latestAssessment)
            : 0
        ),
        instagramFollowing: isZero(Number(values.instagramFollowing)),
        facebookFollowing: isZero(Number(values.facebookFollowing)),
        tiktokFollowing: isZero(Number(values.tiktokFollowing)),
        twitterFollowing: isZero(Number(values.twitterFollowing)),
        youtubeFollowing: isZero(Number(values.youtubeFollowing)),
        designRequestsPerMonthLimit:
          Number(values.designRequestsPerMonthLimit) || 1,
        signing: values.signing || null,
        socialPost: values.socialPost || null,
        appearance: values.appearance || null,
        pressConferenceOrAmplifiedInterview:
          values.pressConferenceOrAmplifiedInterview || null,
        watchParty: values.watchParty || null,
        affinities: values.affinities || null,
        currentSponsors: values.currentSponsors || null,
        conflictsOfInterestsOrNonInterests:
          values.conflictsOfInterestsOrNonInterests || null,
      },
    };

    try {
      const res = await editStoreAdmin({
        variables: {
          input: storeEditInput,
        },
      });

      const errors = res?.data?.editStoreAdmin?.errors;

      if (errors && errors.length > 0) {
        const errorMessages = errors.map((error) => error).join(', ');
        return errorNotification(`${errorMessages}`);
      }

      successNotification(`The description has been updated`);
    } catch (err) {
      if (
        (err as any).graphQLErrors[0]?.extensions.response?.message.some(
          (item: string | string[]) =>
            item.includes('must be a valid phone number')
        )
      ) {
        return errorNotification('Please enter a valid phone number');
      } else {
        errorNotification();
        console.log('update description error:', { ...(err as Error) });
      }
    }
  };

  const adminsOptions = adminsData?.admins?.entities.map(
    ({ id, firstName, lastName, status }) => {
      const isInactive = status === StoreStatus.Inactive;
      return (
        <Option key={id} value={id}>
          <div className={isInactive ? 'isDisabled' : ''}>
            {`${firstName} ${lastName} ${isInactive ? 'INACTIVE' : ''}`}
          </div>
        </Option>
      );
    }
  );

  const influencerCategoryOptions =
    influencerCategoriesData?.getInfluencerCategories.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));

  const areasOfInfluenceOptions =
    areaOfInfluenceData?.getAllAreasOfInfluence?.entities.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));

  const psychosocialKeywordOptions =
    psychosocialKeywordData?.getAllUserPsychosocialKeywords?.entities.map(
      (item) => (
        <Option key={item.id} value={item.id}>
          {item.name}
        </Option>
      )
    );

  const genderOptions = PROFILE_GENDER_OPTIONS.map(({ value, label }) => (
    <Option key={value} value={value}>
      {label}
    </Option>
  ));

  const hashtagsOptions = data?.getHashtags.map((item) => (
    <Option key={item.id} value={item.id}>
      {item.name}
    </Option>
  ));

  const redirectOptions = contentCreator?.redirectSlugs?.map((item) => (
    <Option key={item} value={item}>
      {item}
    </Option>
  ));

  const isContentCreatorInPendingStatus = !contentCreator?.activatedAt;
  const sports = sportsData?.sports || [];

  const handleChangeDescription = (value: string): void => {
    form.setFieldsValue({ description: value });
  };

  const normFile = (e: any) => {
    if (e && Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const setFields = () => {
    const fields = form.getFieldsValue();
    setFormFields({
      ...fields,
    });
  };

  const onPhoneNumberChange = (value: string) =>
    form.setFieldsValue({ contactsPhoneNumber: `+${value}` });

  return (
    <div className="formWrapper">
      <Form
        onFinish={handleFormSubmit}
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 10 }}
        onValuesChange={setFields}
        scrollToFirstError
      >
        <Form.Item>
          <Title level={5}>General</Title>
        </Form.Item>
        <>
          <Form.Item name="storeName" label="Content Creator Name">
            <Input
              placeholder="Enter content creator name"
              disabled={isContentCreatorInPendingStatus}
            />
          </Form.Item>
          <Form.Item name="socialMediaLink" label="Social Media Link">
            <Input placeholder="Enter social media link" />
          </Form.Item>

          <Form.Item name="facebookLink" label="Facebook link">
            <Input placeholder="Enter facebook link" />
          </Form.Item>

          <Form.Item name="facebookFollowing" label="Facebook following">
            <Input type="number" placeholder="Facebook following" />
          </Form.Item>

          <Form.Item name="instagramLink" label="Instagram link">
            <Input placeholder="Enter instagram link" />
          </Form.Item>

          <Form.Item name="instagramFollowing" label="Instagram Following">
            <Input type="number" placeholder="Instagram Following" />
          </Form.Item>

          <Form.Item name="tiktokLink" label="Tiktok link">
            <Input placeholder="Enter tiktok link" />
          </Form.Item>

          <Form.Item name="tiktokFollowing" label="Tiktok Following">
            <Input type="number" placeholder="Tiktok Following" />
          </Form.Item>

          <Form.Item name="twitterLink" label="Twitter link">
            <Input placeholder="Enter Twitter link" />
          </Form.Item>

          <Form.Item name="twitterFollowing" label="Twitter Following">
            <Input type="number" placeholder="Twitter Following" />
          </Form.Item>

          <Form.Item name="youtubeLink" label="Youtube link">
            <Input placeholder="Enter Youtube link" />
          </Form.Item>

          <Form.Item name="youtubeFollowing" label="Youtube Following">
            <Input type="number" placeholder="Youtube Following" />
          </Form.Item>

          <Form.Item name="website" label="Website link">
            <Input placeholder="Enter website link" />
          </Form.Item>
          <Form.Item
            name="sportType"
            label="Type of sport"
            rules={[
              {
                required: true,
                message: 'Please, select sport',
              },
            ]}
          >
            <Select
              id="sportType"
              placeholder="Select sport type"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {sports.map((item) => {
                return (
                  <Option value={`${item.id}`} key={item.id}>
                    {item.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="uploadAvatarImage"
            label="Profile Image"
            validateStatus={
              pictureValidation ||
              form.getFieldsError(['uploadAvatarImage'])[0].errors?.length ||
              !uploadAvatarImage
                ? 'error'
                : ''
            }
            help={pictureValidation || null}
            rules={[
              {
                required: true,
                message: 'Please upload the picture',
              },
            ]}
          >
            <CroppedPictureInput
              setPictureValidation={setPictureValidation}
              defaultImage={contentCreator?.storeDetails?.avatarURL}
              requiredMessage="Please upload the picture"
              buttonTitle="Upload profile picture"
            />
          </Form.Item>

          <Form.Item
            name="uploadCoverImage"
            label="Background image"
            valuePropName="file"
            getValueFromEvent={normFile}
          >
            <UploadImageFile
              hideButton={
                form.getFieldsValue()?.uploadCoverImage?.length >= 1 ||
                form.getFieldInstance('uploadCoverImage')?.fileList?.length
              }
              buttonTitle="Upload image"
              imageAlt="Background image"
              defaultFileUrl={contentCreator?.storeDetails?.coverURL}
            />
          </Form.Item>
          <Form.Item
            name="isOrphanPage"
            label="Hide my profile"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            name="isForBrandDealsOnly"
            label={
              <span>
                Is For Brand Deals Only
                <ProfileVisibilityInfo />
              </span>
            }
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <div className="richTextWrapper">
              <RichText
                onChange={handleChangeDescription}
                placeholder="Add description"
                initialValue={contentCreator?.storeDetails?.description || ''}
              />
            </div>
          </Form.Item>
          <Form.Item
            name="designRequestsPerMonthLimit"
            label="Request Merch Design limit"
            tooltip="How many times per month content creator can request merch design, default value is 1"
          >
            <Input
              type="number"
              placeholder="Enter Request Merch Design limit per month"
              min={1}
              max={100}
            />
          </Form.Item>

          <Title level={5}>Store management</Title>

          <div className="liveDateWrapper">
            <Title level={5}>
              Live date:
              {contentCreator?.storeManagementDetails?.liveDate
                ? moment(
                    contentCreator?.storeManagementDetails?.liveDate
                  ).format(commonTimeFormat)
                : ' Date unavailable'}
            </Title>
          </div>

          <Form.Item name="managedById" label="Managed by:">
            <Select allowClear placeholder="Managed by">
              {adminsOptions}
            </Select>
          </Form.Item>

          <Form.Item
            name="adConnected"
            label="Ad connected"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>

          <Form.Item
            name="socialConnected"
            label="Social connected"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>

          <Form.Item name="agencyName" label="Agency Name">
            <Input placeholder="Enter agency name" />
          </Form.Item>

          <Form.Item name="agencyWebsite" label="Agency Website">
            <Input placeholder="Enter agency Website" />
          </Form.Item>

          <Form.Item name="agentEmail" label="Agent email">
            <Input placeholder="Enter agent email" />
          </Form.Item>

          <Form.Item name="agentName" label="Agent Name">
            <Input placeholder="Enter agent name" />
          </Form.Item>

          <Form.Item name="gender" label="Gender">
            <Select placeholder="Select gender">{genderOptions}</Select>
          </Form.Item>

          <Form.Item name="inbound" label="Inbound">
            <Input placeholder="Enter Inbound" />
          </Form.Item>

          <Form.Item name="initialAssessment" label="Initial Assessment">
            <InputNumber
              type="number"
              placeholder="Initial Assessment"
              min={0}
              max={5}
            />
          </Form.Item>

          <Form.Item name="latestAssessment" label="Latest Assessment">
            <InputNumber
              type="number"
              placeholder="Latest Assessment"
              min={0}
              max={5}
            />
          </Form.Item>

          <Form.Item name="hashtags" label="Hashtags">
            <Select
              placeholder="Select hashtags"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {hashtagsOptions}
            </Select>
          </Form.Item>

          <Form.Item name="areasOfInfluence" label="Areas of interest">
            <Select
              placeholder="Select areas"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {areasOfInfluenceOptions}
            </Select>
          </Form.Item>

          <Form.Item name="psychosocialKeywords" label="Internal tags">
            <Select
              placeholder="Select psychosocial keywords"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {psychosocialKeywordOptions}
            </Select>
          </Form.Item>

          <Form.Item name="influencerCategoryIds" label="Influencer categories">
            <Select
              placeholder="Select influencer categories"
              mode="multiple"
              filterOption={(inputValue, option) =>
                option?.props.children
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            >
              {influencerCategoryOptions}
            </Select>
          </Form.Item>
        </>

        <Form.Item>
          <Title level={5} style={{ marginTop: '16px' }}>
            Contact person
          </Title>
        </Form.Item>
        <>
          <Form.Item
            name="contactsFirstName"
            label="Contact's first Name"
            rules={[
              {
                required: true,
                message: 'Please, input first name',
              },
            ]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
          <Form.Item
            name="contactsLastName"
            label="Contact's last Name"
            rules={[
              {
                required: true,
                message: 'Please, input last name',
              },
            ]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>

          <Form.Item name="contactsRole" label="Contact's role">
            <Input placeholder="Enter role" />
          </Form.Item>
          <Form.Item
            name="contactsEmail"
            label="Contact's email"
            rules={[
              {
                required: true,
                message: 'Please, input email',
              },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            name="contactsPhoneNumber"
            label="Contact's phone number"
            rules={[
              {
                required: true,
                message: 'Please, input phone number',
              },
              {
                message: 'Phone must be a valid phone number',
                validator: async (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (isValidPhoneNumber(value)) {
                    return Promise.resolve();
                  }

                  return Promise.reject();
                },
              },
            ]}
          >
            <PhoneNumberInput
              name="contactsPhoneNumber"
              value={contentCreator?.phoneNumber || ' '}
              placeholder="Enter phone number"
              handlePhoneChange={onPhoneNumberChange}
            />
          </Form.Item>

          <Form.Item name="country" label="Country">
            <Input disabled />
          </Form.Item>

          {form.getFieldValue('state') && (
            <Form.Item name="state" label="State">
              <Input disabled />
            </Form.Item>
          )}

          <Form.Item name="city" label="City">
            <Input disabled />
          </Form.Item>

          <Form.Item name="latitude" noStyle />
          <Form.Item name="longitude" noStyle />

          <Form.Item name="citySearch" label="Enter location">
            <PlaceInput
              onPlaceSelect={(placeDetails) =>
                handlePlaceSelect(form.setFieldsValue, placeDetails, 'city')
              }
            />
          </Form.Item>

          <Form.Item name="redirectSlugs" label="List of redirect names">
            <Select mode="tags" placeholder="Enter redirect name">
              {redirectOptions}
            </Select>
          </Form.Item>

          <Title level={5}>Sponsor pricing</Title>

          <Form.Item
            name="signing"
            label="Signing (hourly rate or price per item and minimums)"
          >
            <Input placeholder="Signing (hourly rate or price per item and minimums)" />
          </Form.Item>

          <Form.Item name="appearance" label="Appearance (hourly rate)">
            <Input placeholder="Appearance (hourly rate)" />
          </Form.Item>

          <Form.Item name="socialPost" label="Social Post (price per post)">
            <Input placeholder="Social Post (price per post)" />
          </Form.Item>

          <Form.Item
            name="pressConferenceOrAmplifiedInterview"
            label="Press Conference / Amplified Interview"
          >
            <Input placeholder="Press Conference / Amplified Interview" />
          </Form.Item>

          <Form.Item name="watchParty" label="Watch Party">
            <Input placeholder="Watch Party" />
          </Form.Item>

          <Form.Item name="affinities" label="Affinities">
            <Input placeholder="Affinities" />
          </Form.Item>

          <Form.Item name="currentSponsors" label="Current Sponsors">
            <Input placeholder="Current Sponsors" />
          </Form.Item>

          <Form.Item
            name="conflictsOfInterestsOrNonInterests"
            label="Conflicts of interest / Non-interests"
          >
            <Input placeholder="Conflicts of interest / Non-interests" />
          </Form.Item>
        </>

        <Form.Item {...buttonItemLayout}>
          <Button type="primary" htmlType="submit" loading={editLoading}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditContentCreatorForm;
